import React, { createContext, useContext, useState, useMemo, type ReactNode } from "react";
import { useGetUserNotificationsQuery } from "../graphql/generated/graphql";

type NotificationContextType = {
  notificationsVisible: boolean;
  setNotificationsVisible: (visible: boolean) => void;
  toggleNotifications: () => void;
  notifications: Array<{
    id: string;
    isRead: boolean;
    title: string;
    message: string;
    created: string;
    actionUrl?: string | null;
    type?: string;
  }>;
  hasUnreadNotification: boolean;
  refetch: () => void;
};

const NotificationContext = createContext<NotificationContextType>({
  notificationsVisible: false,
  setNotificationsVisible: () => {},
  toggleNotifications: () => {},
  notifications: [],
  hasUnreadNotification: false,
  refetch: () => {},
});

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const [notificationsVisible, setNotificationsVisible] = useState(false);

  const { data: notificationsData, refetch } = useGetUserNotificationsQuery({
    pollInterval: 30000,
    fetchPolicy: "network-only",
  });

  const notifications = notificationsData?.getUserNotifications?.data ?? [];

  const hasUnreadNotification = useMemo(() => {
    return notifications.some((notification) => notification.isRead !== true);
  }, [notifications]);

  const toggleNotifications = () => {
    setNotificationsVisible((prev) => !prev);
  };

  return (
    <NotificationContext.Provider
      value={{
        notificationsVisible,
        setNotificationsVisible,
        toggleNotifications,
        notifications,
        hasUnreadNotification,
        refetch,
      }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  return useContext(NotificationContext);
};
